import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Кабінет ДПС → «створити»: КАТАЛОГ типів документів для відправки у ДПС.
 * Бухгалтер шукає потрібний документ у полі пошуку → обирає → потрапляє на
 * окрему сторінку створення саме цього документа. Кожен тип має свою
 * сторінку (податковий розрахунок, сервісний запит, повідомлення тощо).
 */

interface DocType {
  key: string;
  title: string;
  code: string;        // код форми/родини — для пошуку
  keywords: string;    // додаткові слова для пошуку
  route: string;       // сторінка створення
  group: string;
}

const DOC_TYPES: DocType[] = [
  {
    key: 'j05',
    title: 'Податковий розрахунок сум доходу (ПДФО / ВЗ / ЄСВ)',
    code: 'J0500110',
    keywords: 'об\'єднана звітність єдиний внесок пдфо військовий збір 4дф '
      + 'д1 д5 зарплата найманих працівників',
    route: '/tax-reports-j05/new',
    group: 'Звітність',
  },
  {
    key: 'dps-request',
    title: 'Сервісний запит до ДПС (витяг, стан розрахунків)',
    code: 'J1300208',
    keywords: 'запит витяг стан розрахунків з бюджетом пеня платежі '
      + 'інформаційна довідка',
    route: '/dps-cabinet/requests/new',
    group: 'Запити',
  },
  {
    key: 'hire-notif',
    title: 'Повідомлення про прийняття працівника на роботу',
    code: 'J3001005',
    keywords: 'повідомлення прийняття на роботу працівник кадри '
      + 'до початку роботи',
    route: '/hire-notifications/new',
    group: 'Кадри',
  },
];

export default function DpsCabinetNewPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return DOC_TYPES;
    return DOC_TYPES.filter((d) =>
      `${d.title} ${d.code} ${d.keywords} ${d.group}`.toLowerCase().includes(q));
  }, [search]);

  // Групування за розділом (для наочності каталога).
  const groups = useMemo(() => {
    const m = new Map<string, DocType[]>();
    for (const d of filtered) {
      (m.get(d.group) ?? m.set(d.group, []).get(d.group)!).push(d);
    }
    return [...m.entries()];
  }, [filtered]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Створити документ для ДПС</h2>
        <button style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/dps-cabinet')}>до Кабінету</button>
      </div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Оберіть тип документа для подання у ДПС. Почніть вводити назву —
        список відфільтрується.
      </p>

      <input
        autoFocus
        value={search}
        placeholder="напр. «податковий розрахунок», «витяг», «прийняття на роботу»"
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && filtered.length === 1) {
            navigate(filtered[0]!.route);
          }
        }}
        style={{ width: '100%', maxWidth: 620, padding: '0.5rem 0.7rem',
          fontSize: '0.95rem', marginBottom: '1rem' }}
      />

      {groups.length === 0 && (
        <div style={{ color: 'var(--muted)' }}>Нічого не знайдено.</div>
      )}

      {groups.map(([group, items]) => (
        <div key={group} style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--muted)',
            textTransform: 'uppercase', letterSpacing: '0.03em',
            marginBottom: '0.35rem' }}>{group}</div>
          <div style={{ display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '0.6rem' }}>
            {items.map((d) => (
              <button key={d.key}
                onClick={() => navigate(d.route)}
                style={{ textAlign: 'left', padding: '0.7rem 0.85rem',
                  border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff',
                  cursor: 'pointer', display: 'flex', flexDirection: 'column',
                  gap: 3 }}>
                <span style={{ fontWeight: 600 }}>{d.title}</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--muted)',
                  fontFamily: 'monospace' }}>{d.code}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
